import Link from 'next/link';
import { commifyNumber } from '../../../../../helpers/common';
import styled from 'styled-components';
import { useTranslation } from 'next-i18next';
import { logEvent } from '../../../../../new-lib/analytics';
import { useRouter } from 'next/router';
import ImageWrapper from '../../../../../new-components/ImageWrapper';
import { useSelector } from 'react-redux';
import { LaunchDTO } from '../../dtos/response.dtos';

interface LaunchCardProps {
  entity: LaunchDTO;
  eventLocation: string;
  index: number;
}

const LaunchPageCard = ({ entity, eventLocation, index }: LaunchCardProps) => {
  const { t } = useTranslation(['launches', 'common']);
  const { query } = useRouter();
  const webview = useSelector((state: any) => state.app.webview);
  const isMobile = useSelector((state: any) => state.app.isMobile);

  const getURL = () => {
    if (webview === 'partners') {
      return `/new-launches/${entity?.slug}`;
    }
    return `/compound/${entity?.slug}`;
  };

  return (
    <Link
      href={{ pathname: getURL(), query: query.partners && { partners: true } }}
    >
      <Container
        onClick={() =>
          logEvent.click('lnch_click', eventLocation, 'launch', {
            lnch_id: Number(entity.slug.split('-')[0]),
            lnch_name: entity.name,
            org_id: entity.nawyOrganizationId ?? 1
          })
        }
      >
        <div className="upper-card">
          <ImageWrapper
            src={entity?.imageUrl}
            fill
            alt={`${entity?.name} Marketing Visual`}
            style={{ borderRadius: '8px' }}
            withSkeleton
            priority={isMobile && index < 2 ? true : index < 6 ? true : false}
            imageSize="high"
            sizes="50vw"
          />
        </div>
        <div className="lower-card">
          <span className="launch-name">{entity?.name}</span>
          <p className="area-name">{entity?.areaName}</p>
          {entity?.minPrice > 0 && (
            <div className="price-section">
              <span className="price-amount">
                {commifyNumber(entity?.minPrice)} {entity?.currency || t('egp')}
              </span>
              <span className="start-price">
                {t('start_price', { ns: 'common' })}
              </span>
            </div>
          )}
        </div>
      </Container>
    </Link>
  );
};

export default LaunchPageCard;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: auto;
  width: 100%;
  color: ${props => props.theme.light.colors.black};
  cursor: pointer;
  user-select: none;

  .upper-card {
    position: relative;
    width: 100%;
    aspect-ratio: 16/9;

    .card-badges {
      position: absolute;
      bottom: 8px;
      inset-inline-end: 8px;
    }
  }

  .lower-card {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 15px 0px;

    .launch-name {
      font-size: ${props => props.theme.font.sizes.xl};
      font-family: materialSemiBold;
      text-transform: capitalize;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      display: block;
      max-width: calc(100vw - 40px);
    }

    .area-name {
      font-size: ${props => props.theme.font.sizes.xs};
    }

    .price-section {
      display: flex;
      align-items: center;
      gap: 8px;
      .start-price {
        font-size: 13px;
        color: #b6b6b6;
      }

      .price-amount {
        font-family: 'materialBold';
        font-size: ${props => props.theme.font.sizes.xl};
      }
    }
  }

  @media screen and (max-width: ${props =>
      props.theme.breakPoints.mobile.max}) {
    .lower-card {
      .area-name {
        font-size: ${props => props.theme.font.sizes.xxs};
      }
      .launch-name {
        font-size: ${props => props.theme.font.sizes.m};
      }

      .price-section {
        .price-amount {
          font-size: ${props => props.theme.font.sizes.xxl};
          font-family: materialBold;
        }
      }
    }
  }
`;
